import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser, useClerk } from '@clerk/react';
import InputForm from '../components/InputForm';
import LogoutModal from '../components/LogoutModal';
import HistorySidebar from '../components/HistorySidebar';
import SessionCounter from '../components/SessionCounter';
import { MessageSquare, LogOut, History } from 'lucide-react';
import Footer from '../components/Footer';
import './Dashboard.css';
import { API_BASE_URL } from '../config';

const Dashboard = () => {
    const { isLoaded, isSignedIn, getToken, has } = useAuth();
    const { user } = useUser();
    const { signOut } = useClerk();

    // Active paid plan gates access to the app.
    const hasPlan = isLoaded && isSignedIn && !!has &&
        (has({ plan: 'basic_subscription' }) || has({ plan: 'premium_subscription' }));

    const userName = user?.firstName
        || user?.fullName
        || user?.primaryEmailAddress?.emailAddress?.split('@')[0]
        || 'User';

    const navigate = useNavigate();
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [searchHistory, setSearchHistory] = useState([]);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [selectedResult, setSelectedResult] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [usage, setUsage] = useState(null);

    const fetchHistory = useCallback(async () => {
        if (!isSignedIn) return;
        setHistoryLoading(true);
        try {
            const token = await getToken();
            const response = await fetch(`${API_BASE_URL}/queries/my`, {
                credentials: 'include',
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            const data = await response.json();
            if (data.items) {
                setSearchHistory(data.items);
            }
        } catch (error) {
            console.error('Failed to fetch history:', error);
        } finally {
            setHistoryLoading(false);
        }
    }, [getToken, isSignedIn]);

    const fetchUsage = useCallback(async () => {
        if (!isSignedIn) return;
        try {
            const token = await getToken();
            const response = await fetch(`${API_BASE_URL}/usage`, {
                credentials: 'include',
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (response.ok) {
                setUsage(await response.json());
            } else {
                console.warn('Usage fetch returned', response.status, await response.text().catch(() => ''));
            }
        } catch (error) {
            console.error('Failed to fetch usage:', error);
        }
    }, [getToken, isSignedIn]);

    useEffect(() => {
        if (hasPlan) {
            fetchHistory();
            fetchUsage();
        }
    }, [hasPlan, fetchHistory, fetchUsage]);

    // Auth + subscription gate: not signed in -> login; signed in but unpaid -> pricing.
    useEffect(() => {
        if (!isLoaded) return;
        if (!isSignedIn) {
            navigate('/login');
        } else if (!hasPlan) {
            navigate('/verified');
        }
    }, [isLoaded, isSignedIn, hasPlan, navigate]);

    const handleQueryComplete = () => {
        fetchHistory(); // Refresh history after new query
        fetchUsage();   // Refresh the usage counter after a query is consumed
    };

    const handleSelectHistory = (item) => {
        // Map history item back to result format
        // Backend now returns the full verdict object and correct field names (truth_score)
        setSelectedResult({
            ...item,
            // Backend returns verdict_full for rich metadata, fallback to verdict string
            verdict: item.verdict_full || (typeof item.verdict === 'string' ? { verdict_label: item.verdict } : item.verdict)
        });

        // Scroll to result or just let InputForm handle it
        if (window.innerWidth <= 768) {
            setIsHistoryOpen(false);
        }
    };

    const handleLogoutConfirm = async () => {
        setShowLogoutModal(false);
        await signOut();
        navigate('/login');
    };

    if (!isLoaded) {
        return (
            <div className="dashboard-loading">
                <div className="loader"></div>
                <p>Syncing session...</p>
            </div>
        );
    }

    // While the gate effect redirects unsigned/unpaid users, render nothing.
    if (!isSignedIn || !hasPlan) return null;

    return (
        <div className="dashboard-container">
            <header className="dashboard-header glass">
                <div className="header-left" onClick={() => navigate('/app')} style={{ cursor: 'pointer' }}>
                    <img src="/images/logo_color.jpg" alt="VerDict AI" className="header-logo-img" />
                </div>

                <div className="header-right">
                    {usage && (
                        <SessionCounter
                            queriesUsed={usage.queries_used}
                            queriesRemaining={usage.queries_remaining}
                        />
                    )}

                    <button
                        onClick={() => setIsHistoryOpen(!isHistoryOpen)}
                        className={`header-feedback-btn ${isHistoryOpen ? 'active' : ''}`}
                        title="Search History"
                    >
                        <History size={18} />
                        <span>History</span>
                    </button>

                    <button onClick={() => navigate('/feedback')} className="header-feedback-btn">
                        <MessageSquare size={18} />
                        <span>Feedback</span>
                    </button>

                    <div className="user-profile">
                        <div className="user-avatar">
                            {userName ? userName.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <span className="user-name">{userName}</span>
                        <button className="icon-btn logout-trigger" onClick={() => setShowLogoutModal(true)}>
                            <LogOut size={18} />
                        </button>
                    </div>
                </div>
            </header>

            <HistorySidebar
                history={searchHistory}
                isOpen={isHistoryOpen}
                activeId={selectedResult?.id}
                onSelect={handleSelectHistory}
                onClose={() => setIsHistoryOpen(false)}
                loading={historyLoading}
            />

            <main className={`dashboard-main ${isHistoryOpen ? 'shifted' : ''}`}>
                <div className="dashboard-content">
                    <div className="welcome-section">
                        <h1>What would you like to verify today?</h1>
                        <p>Analyze links, text, or images for factual accuracy.</p>
                    </div>

                    <InputForm
                        onQueryComplete={handleQueryComplete}
                        onLimitReached={() => navigate('/blocked')}
                        initialResult={selectedResult}
                    />
                </div>
            </main>

            {showLogoutModal && (
                <LogoutModal
                    onConfirm={handleLogoutConfirm}
                    onCancel={() => setShowLogoutModal(false)}
                />
            )}

            <Footer />
        </div>
    );
};

export default Dashboard;
